import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import { canonicalMoney, sumMoney } from '../../lib/money';
import { useCreateScheduleEntry } from '../../hooks/useCreateScheduleEntry';
import { log as logger } from '../../lib/logger';
import { lookupErrorCopy, mapPostgresError, type MoneyErrorCode } from '../../lib/errorMap';
import { useAuthStore } from '../../stores/authStore';
import { localWallClockToInstant } from '../../components/schedule/scheduleHelpers';
import type { Booking, Service } from '../../types';

async function log(biz: string, type: string, desc: string, eId: string, uid: string) {
  try { await supabase.from('activity_log').insert({ business_id: biz, activity_type: type, description: desc, entity_type: 'booking', entity_id: eId, logged_by: uid }); } catch { /* */ }
}

export interface BookingRow extends Booking {
  service_name?: string;
  assigned_name?: string;
}

export interface BookingSummary {
  todayCount: number;
  weekCount: number;
  noShowsMonth: number;
  revenueMonth: number;
}

interface TeamUser { id: string; first_name: string; last_name: string; }

export function useBookings(businessId: string | undefined) {
  const [all, setAll] = useState<BookingRow[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const createScheduleEntry = useCreateScheduleEntry();

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      const [bkRes, svcRes, uRes] = await Promise.all([
        supabase.from('bookings').select('*').eq('business_id', businessId)
          .order('date', { ascending: false }).limit(200).abortSignal(ctrl.signal),
        supabase.from('services').select('id, name, duration_minutes, price')
          .eq('business_id', businessId).eq('is_active', true).order('name').abortSignal(ctrl.signal),
        supabase.from('users').select('id, first_name, last_name').eq('business_id', businessId)
          .eq('status', 'active').abortSignal(ctrl.signal),
      ]);
      if (bkRes.error) throw bkRes.error;
      const svcMap = new Map((svcRes.data ?? []).map(s => [s.id, s.name]));
      const uMap = new Map((uRes.data ?? []).map(u => [u.id, `${u.first_name} ${u.last_name}`]));
      const rows = (bkRes.data ?? []).map(b => ({
        ...b,
        service_name: b.service_id ? svcMap.get(b.service_id) ?? null : null,
        assigned_name: b.assigned_id ? uMap.get(b.assigned_id) ?? null : null,
      })) as BookingRow[];
      if (mountedRef.current) { setAll(rows); setServices((svcRes.data ?? []) as Service[]); setUsers((uRes.data ?? []) as TeamUser[]); }
    } catch (e) { if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed'); }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  useEffect(() => { mountedRef.current = true; void fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  const rt = useMemo(() => ['bookings'], []);
  useRealtimeSubscription(businessId, rt, fetch);

  const summary = useMemo<BookingSummary>(() => {
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
    const monthStart = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;
    return {
      todayCount: all.filter(b => b.date === today && b.status !== 'cancelled').length,
      weekCount: all.filter(b => b.date >= weekAgo && b.status !== 'cancelled').length,
      noShowsMonth: all.filter(b => b.date >= monthStart && b.status === 'no_show').length,
      revenueMonth: Number(sumMoney(all.filter(b => b.date >= monthStart && b.status === 'completed')
        .map(b => canonicalMoney(b.payment_amount ?? 0)))),
    };
  }, [all]);

  const createBooking = useCallback(async (data: Partial<Booking> & { service_name?: string }, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { data: bk, error: e } = await supabase.from('bookings').insert({
        ...data, business_id: businessId, source: data.source || 'admin_panel',
      }).select('id').maybeSingle();
      if (e || !bk) return false;
      // Schedule projection via canonical RPC.
      if (data.date && data.start_time) {
        const title = data.service_name ? `${data.service_name} — ${data.client_name ?? ''}` : (data.client_name ?? 'Booking');
        // start_at/end_at are timestamptz — build true instants at the business tz
        // (A2 P2), not naive wall-clock-as-UTC strings.
        const tz = useAuthStore.getState().business?.timezone ?? null;
        const entryRes = await createScheduleEntry({
          type: 'booking',
          title,
          startAt: localWallClockToInstant(data.date, `${data.start_time}:00`, tz),
          endAt: localWallClockToInstant(data.date, `${data.end_time || data.start_time}:00`, tz),
          assignedTo: data.assigned_id ? [data.assigned_id] : undefined,
          bookingId: bk.id,
          idempotencyKey: crypto.randomUUID(),
        });
        if (!entryRes.success) {
          logger.warn('Schedule entry creation failed for booking:', bk.id, entryRes.error.code);
        }
      }
      await log(businessId, 'booking_created', `Booking created: ${data.client_name}`, bk.id, userId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch, createScheduleEntry]);

  const updateStatus = useCallback(async (id: string, status: string, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const updates: Record<string, unknown> = { status };
      if (status === 'completed') updates.completed_at = new Date().toISOString();
      const { error: e } = await supabase.from('bookings').update(updates).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, `booking_${status}`, `Booking ${status}`, id, userId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  const recordPayment = useCallback(async (id: string, method: string, amount: number, tip: number, _userId: string): Promise<boolean> => {
    if (!businessId) return false;
    // Two-step write per D2.7: RPC owns payment_status / payment_amount /
    // ledger / activity_log atomically; tip lives only on bookings.tip_amount
    // and is updated separately after the RPC succeeds. Mirrors §2.15
    // useCheckout shape. Migration 294 (Decisions #582/#583) dropped
    // p_recorded_by — actor is server-derived; envelope returns replace
    // RAISE EXCEPTION patterns.
    const { data: rpcData, error: rpcErr } = await supabase.rpc('record_booking_payment', {
      p_booking_id: id,
      p_amount: amount,
      p_method: method,
    });
    if (rpcErr) {
      logger.error('record_booking_payment RPC failed', rpcErr, {
        friendly: lookupErrorCopy('record_booking_payment', mapPostgresError(rpcErr).code),
      });
      return false;
    }
    const env = rpcData as { success?: boolean; error?: { code?: string } } | null;
    if (!env?.success) {
      const code = (env?.error?.code ?? 'UNKNOWN') as MoneyErrorCode;
      logger.error('record_booking_payment envelope error', undefined, {
        code,
        friendly: lookupErrorCopy('record_booking_payment', code),
      });
      return false;
    }
    if (tip > 0) {
      const { error: tipErr } = await supabase.from('bookings').update({ tip_amount: tip }).eq('id', id);
      if (tipErr) {
        logger.error('booking tip_amount update failed after RPC success:', tipErr);
        // RPC + ledger already correct. Surface to caller; tip is recoverable.
        void fetch();
        return false;
      }
    }
    void fetch(); return true;
  }, [businessId, fetch]);

  const closeBookingWithoutPayment = useCallback(async (id: string, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    const { error: updateErr } = await supabase.from('bookings')
      .update({ payment_status: 'closed_unpaid' })
      .eq('id', id)
      .eq('business_id', businessId);
    if (updateErr) { logger.error('closeBookingWithoutPayment failed:', updateErr); return false; }
    await log(businessId, 'booking_closed_unpaid', 'Closed without payment', id, userId);
    void fetch();
    return true;
  }, [businessId, fetch]);

  return { bookings: all, services, users, loading, error, summary, refresh: fetch, createBooking, updateStatus, recordPayment, closeBookingWithoutPayment };
}

// ── Appointment Grid Hook ──

export interface GridResource {
  id: string;
  name: string;
  assigned_user_id: string | null;
  assigned_name?: string;
  color?: string | null;
  sort_order: number;
}

export interface GridBusinessHours {
  open_time: string;
  close_time: string;
}

export function useAppointmentGrid(businessId: string | undefined, date: string) {
  const [resources, setResources] = useState<GridResource[]>([]);
  const [businessHours, setBusinessHours] = useState<GridBusinessHours>({ open_time: '09:00', close_time: '17:00' });
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetchGrid = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      const dow = new Date(date + 'T12:00:00').getDay();
      const [rRes, hRes] = await Promise.all([
        supabase.from('schedule_resources').select('id, name, assigned_user_id, color, sort_order')
          .eq('business_id', businessId).eq('active', true).order('sort_order'),
        supabase.from('business_hours').select('open_time, close_time')
          .eq('business_id', businessId).eq('day_of_week', dow).eq('is_open', true).maybeSingle(),
      ]);

      const rows = (rRes.data ?? []) as GridResource[];
      // Resolve assigned names
      const userIds = rows.filter(r => r.assigned_user_id).map(r => r.assigned_user_id!);
      if (userIds.length > 0) {
        const { data: users } = await supabase.from('users').select('id, first_name, last_name').in('id', userIds);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const map = new Map((users ?? []).map((u: any) => [u.id, `${u.first_name} ${u.last_name}`]));
        for (const r of rows) {
          if (r.assigned_user_id && map.has(r.assigned_user_id)) r.assigned_name = map.get(r.assigned_user_id);
        }
      }

      if (mountedRef.current) {
        setResources(rows);
        if (hRes.data) setBusinessHours({ open_time: hRes.data.open_time, close_time: hRes.data.close_time });
      }
    } catch { /* silent */ }
    finally { if (mountedRef.current) setLoading(false); }
  }, [businessId, date]);

  useEffect(() => { mountedRef.current = true; void fetchGrid(); return () => { mountedRef.current = false; }; }, [fetchGrid]);

  const rt = useMemo(() => ['schedule_resources', 'business_hours'], []);
  useRealtimeSubscription(businessId, rt, fetchGrid);

  return { resources, businessHours, loading, refresh: fetchGrid };
}
